import React, { useLayoutEffect, useRef, useState } from 'react';
import { css } from '@compiled/react';

import Avatar from '@atlaskit/avatar/avatar';
import { cssMap, cx } from '@atlaskit/css';
import { Box } from '@atlaskit/primitives/compiled/box';
import { token } from '@atlaskit/tokens';

import { expVal } from '@confluence/feature-experiments';

import { type AgentIconType } from './agentIconType.ts';
import { AgentRowAvatar, type AgentAvatarSize } from './AgentRowAvatar.tsx';
import { isClickableAgentIdentity } from './isClickableAgentIdentity.ts';
import type { AgentPresenceColorMode } from './presenceIconChrome.ts';
import { ProfileIdentity, type ProfileIdentityProps } from './ProfileIdentity.tsx';

const AGENT_USER_DUAL_AVATAR_AGENT_SIZE: AgentAvatarSize = 'small';
const AGENT_USER_DUAL_AVATAR_USER_SIZE = 'xxsmall';

const DEFAULT_AVATAR_ITEM_COVER = { top: 0, left: 0, width: 32, height: 32 };

type AvatarItemCover = typeof DEFAULT_AVATAR_ITEM_COVER;
export type AgentUserDualAvatarSize = 'default' | 'large';
type DualAvatarStack = 'legacy' | 'default' | 'large';

// 14px is not an ADS space token; cssMap rejects it.
const dualAvatarUserDefaultFrameStyles = css({
	position: 'absolute',
	top: '14px',
	insetInlineStart: '14px',
	width: '12px',
	height: '12px',
	overflow: 'visible',
	borderRadius: token('radius.full'),
	boxShadow: `0 0 0 1px var(--agent-user-dual-avatar-user-border, ${token('elevation.surface')})`,
});

// 18px is not an ADS space token; cssMap rejects it.
const dualAvatarUserLargeFrameStyles = css({
	position: 'absolute',
	top: '18px',
	insetInlineStart: '18px',
	width: '16px',
	height: '16px',
	overflow: 'visible',
	borderRadius: token('radius.full'),
	boxShadow: `0 0 0 1px var(--agent-user-dual-avatar-user-border, ${token('elevation.surface')})`,
});

// Avatar xxsmall paints a 16px face with 2px slot margin; clip it so only the face shows.
const dualAvatarUserFaceClipStyles = css({
	width: '16px',
	height: '16px',
	overflow: 'hidden',
	borderRadius: token('radius.full'),
});

const dualAvatarUserAvatarShiftStyles = css({
	marginBlockStart: token('space.negative.025'),
	marginBlockEnd: token('space.negative.025'),
	marginInlineStart: token('space.negative.025'),
	marginInlineEnd: token('space.negative.025'),
});

const dualAvatarCardTriggerStyles = css({
	position: 'absolute',
	display: 'block',
	pointerEvents: 'none',
	boxSizing: 'border-box',
	// Popper measures this Pressable, not the overlay. People-only rows wrap AvatarItem;
	// dual rows must make the same button fill that identity cell. The migrated trigger
	// uses `all: unset; display: inline` and testId `user-profile-card-trigger-wrapper`.
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- override profile-card Pressable box
	'& [data-testid="user-profile-card-trigger-wrapper"], & [data-testid="profileCardTrigger"]': {
		display: 'block',
		width: '100%',
		height: '100%',
		pointerEvents: 'none',
	},
});

const useAvatarItemCover = (): {
	rootRef: React.RefObject<HTMLDivElement>;
	cover: AvatarItemCover;
} => {
	const rootRef = useRef<HTMLDivElement>(null) as React.RefObject<HTMLDivElement>;
	const [cover, setCover] = useState<AvatarItemCover>(DEFAULT_AVATAR_ITEM_COVER);

	useLayoutEffect(() => {
		const root = rootRef.current;
		const identityCell = root?.parentElement;
		if (!root || !identityCell) {
			return;
		}

		const updateCover = () => {
			const identityRect = identityCell.getBoundingClientRect();
			const rootRect = root.getBoundingClientRect();
			if (identityRect.width === 0 && identityRect.height === 0) {
				return;
			}
			setCover({
				top: identityRect.top - rootRect.top,
				left: identityRect.left - rootRect.left,
				width: identityRect.width,
				height: identityRect.height,
			});
		};

		updateCover();
		if (typeof ResizeObserver === 'undefined') {
			return;
		}
		const observer = new ResizeObserver(updateCover);
		observer.observe(identityCell);
		observer.observe(root);
		return () => observer.disconnect();
	}, [rootRef]);

	return { rootRef, cover };
};

const styles = cssMap({
	dualAvatar: {
		position: 'relative',
		width: '32px',
		height: '32px',
		flexShrink: 0,
		marginInlineEnd: token('space.025'),
	},
	dualAvatarOffsetInlineStart: {
		position: 'relative',
		width: '32px',
		height: '32px',
		flexShrink: 0,
		marginInlineEnd: token('space.025'),
		transform: 'translateX(2px)',
	},
	dualAvatarDefault: {
		position: 'relative',
		width: '24px',
		height: '24px',
		flexShrink: 0,
		alignSelf: 'center',
		overflow: 'visible',
		marginBlockStart: token('space.025'),
		marginBlockEnd: token('space.025'),
		marginInlineStart: token('space.025'),
		marginInlineEnd: token('space.025'),
	},
	dualAvatarDefaultOffset: {
		position: 'relative',
		width: '24px',
		height: '24px',
		flexShrink: 0,
		alignSelf: 'center',
		overflow: 'visible',
		marginBlockStart: token('space.025'),
		marginBlockEnd: token('space.025'),
		marginInlineStart: token('space.025'),
		marginInlineEnd: token('space.025'),
	},
	dualAvatarLarge: {
		position: 'relative',
		width: '32px',
		height: '32px',
		flexShrink: 0,
		alignSelf: 'center',
		overflow: 'visible',
		marginBlockStart: token('space.025'),
		marginBlockEnd: token('space.025'),
		marginInlineStart: token('space.025'),
		marginInlineEnd: token('space.025'),
	},
	dualAvatarLargeOffset: {
		position: 'relative',
		width: '32px',
		height: '32px',
		flexShrink: 0,
		alignSelf: 'center',
		overflow: 'visible',
		marginBlockStart: token('space.025'),
		marginBlockEnd: token('space.025'),
		marginInlineStart: token('space.025'),
		marginInlineEnd: token('space.025'),
	},
	dualAvatarAgent: {
		position: 'absolute',
		top: token('space.0'),
		insetInlineStart: token('space.0'),
		width: '20px',
		height: '20px',
		transform: 'scale(0.8333333333333334)',
		transformOrigin: 'top left',
	},
	dualAvatarAgentDefault: {
		position: 'absolute',
		top: token('space.0'),
		insetInlineStart: token('space.0'),
		width: '24px',
		height: '24px',
		transform: 'none',
		overflow: 'visible',
	},
	dualAvatarAgentLarge: {
		position: 'absolute',
		top: token('space.0'),
		insetInlineStart: token('space.0'),
		width: '32px',
		height: '32px',
		transform: 'none',
		overflow: 'visible',
	},
	dualAvatarUser: {
		position: 'absolute',
		top: token('space.150'),
		insetInlineEnd: token('space.025'),
		marginBlockStart: token('space.025'),
		borderRadius: token('radius.full'),
		backgroundColor: token('elevation.surface'),
	},
	dualAvatarUserDefaultInner: {
		width: '16px',
		height: '16px',
		transform: 'scale(0.75)',
		transformOrigin: 'top left',
	},
	dualAvatarTriggerFill: {
		position: 'relative',
		width: '100%',
		height: '100%',
		display: 'block',
		pointerEvents: 'none',
	},
	dualAvatarVisualStack: {
		position: 'absolute',
		width: '32px',
		height: '32px',
		pointerEvents: 'none',
	},
	dualAvatarVisualStackDefault: {
		position: 'absolute',
		width: '24px',
		height: '24px',
		overflow: 'visible',
		pointerEvents: 'none',
	},
	dualAvatarVisualStackLarge: {
		position: 'absolute',
		width: '32px',
		height: '32px',
		overflow: 'visible',
		pointerEvents: 'none',
	},
	dualAvatarTriggerTarget: {
		pointerEvents: 'auto',
	},
});

const stackRootAttrs = (stack: DualAvatarStack) => {
	if (stack === 'default') {
		return {
			'data-width': '24px',
			'data-height': '24px',
			'data-overflow': 'visible',
			'data-slot-margin': 'space.025',
		};
	}
	if (stack === 'large') {
		return {
			'data-width': '32px',
			'data-height': '32px',
			'data-overflow': 'visible',
			'data-slot-margin': 'space.025',
		};
	}
	return {
		'data-width': '32px',
		'data-height': '32px',
	};
};

const stackAgentAttrs = (stack: DualAvatarStack) => {
	if (stack === 'default') {
		return {
			'data-width': '24px',
			'data-height': '24px',
			'data-transform': 'none',
			'data-clip': 'none',
		};
	}
	if (stack === 'large') {
		return {
			'data-width': '32px',
			'data-height': '32px',
			'data-transform': 'none',
			'data-clip': 'none',
		};
	}
	return {
		'data-width': '20px',
		'data-height': '20px',
		'data-transform': 'scale(0.8333333333333334)',
	};
};

const stackUserFrameAttrs = (stack: DualAvatarStack) => {
	if (stack === 'default') {
		return {
			'data-width': '12px',
			'data-height': '12px',
			'data-top': '14px',
			'data-left': '14px',
			'data-overflow': 'visible',
			'data-avatar-slot-shift': 'space.negative.025',
		};
	}
	return {
		'data-width': '16px',
		'data-height': '16px',
		'data-top': '18px',
		'data-left': '18px',
		'data-overflow': 'visible',
		'data-avatar-slot-shift': 'space.negative.025',
	};
};

export const AgentUserDualAvatar = ({
	agentName,
	agentIcon,
	agentAvatarUrl,
	agentBorderColor,
	userBorderColor,
	agentUserId,
	userName,
	userAvatarUrl,
	relatedUserId,
	isOffsetInlineStart = false,
	size = 'default',
	fillProfileClickableFrame = false,
	profileCardTrigger,
	position,
	offset,
	colorMode = 'light',
}: {
	agentName?: string;
	agentIcon?: AgentIconType;
	agentAvatarUrl?: string;
	/** Overrides the large agent's surface-colored outline, e.g. transparent for borderless rows. */
	agentBorderColor?: string;
	/** Defaults to elevation.surface; overlay callers use elevation.surface.overlay for the badge. */
	userBorderColor?: string;
	agentUserId?: string;
	userName?: string;
	userAvatarUrl?: string;
	relatedUserId?: string;
	isOffsetInlineStart?: boolean;
	size?: AgentUserDualAvatarSize;
	fillProfileClickableFrame?: boolean;
	profileCardTrigger?: ProfileIdentityProps['trigger'];
	position?: ProfileIdentityProps['position'];
	offset?: ProfileIdentityProps['offset'];
	colorMode?: AgentPresenceColorMode;
}): React.JSX.Element => {
	const effectiveAgentUserId = expVal('dual_agent_avatar_card', 'isEnabled', false)
		? isClickableAgentIdentity({
				accountId: agentUserId,
				agentIcon,
				displayName: agentName,
			})
			? agentUserId
			: undefined
		: agentUserId;
	const userAvatar = (
		<Avatar
			src={userAvatarUrl}
			name={userName}
			size={AGENT_USER_DUAL_AVATAR_USER_SIZE}
			borderColor="transparent"
			label=""
		/>
	);

	if (size === 'large' && expVal('dual_agent_avatar_card', 'isEnabled', false)) {
		return (
			<DualAvatarWithIdentityCards
				agentName={agentName}
				agentUserId={effectiveAgentUserId}
				userName={userName}
				relatedUserId={relatedUserId}
				isOffsetInlineStart={isOffsetInlineStart}
				stack="large"
				userBorderColor={userBorderColor}
				profileCardTrigger={profileCardTrigger}
				position={position}
				offset={offset}
				agentAvatar={
					<AgentRowAvatar
						name={agentName}
						agentIcon={agentIcon}
						avatarUrl={agentAvatarUrl}
						borderColor={agentBorderColor}
						avatarSize="medium"
						hasInlineSpacing={false}
						fillProfileClickableFrame={
							fillProfileClickableFrame && effectiveAgentUserId !== undefined
						}
						colorMode={colorMode}
					/>
				}
				userAvatar={userAvatar}
			/>
		);
	}

	if (expVal('dual_agent_avatar_card', 'isEnabled', false)) {
		return (
			<DualAvatarWithIdentityCards
				agentName={agentName}
				agentUserId={effectiveAgentUserId}
				userName={userName}
				relatedUserId={relatedUserId}
				isOffsetInlineStart={isOffsetInlineStart}
				stack="default"
				userBorderColor={userBorderColor}
				profileCardTrigger={profileCardTrigger}
				position={position}
				offset={offset}
				agentAvatar={
					<AgentRowAvatar
						name={agentName}
						agentIcon={agentIcon}
						avatarUrl={agentAvatarUrl}
						borderColor={agentBorderColor}
						avatarSize="small"
						hasInlineSpacing={false}
						fillProfileClickableFrame={
							fillProfileClickableFrame && effectiveAgentUserId !== undefined
						}
						colorMode={colorMode}
					/>
				}
				userAvatar={userAvatar}
			/>
		);
	}

	return (
		<DualAvatarWithIdentityCards
			agentName={agentName}
			agentUserId={effectiveAgentUserId}
			userName={userName}
			relatedUserId={relatedUserId}
			isOffsetInlineStart={isOffsetInlineStart}
			stack="legacy"
			profileCardTrigger={profileCardTrigger}
			position={position}
			offset={offset}
			agentAvatar={
				<AgentRowAvatar
					name={agentName}
					agentIcon={agentIcon}
					avatarUrl={agentAvatarUrl}
					avatarSize={AGENT_USER_DUAL_AVATAR_AGENT_SIZE}
					hasInlineSpacing={false}
					colorMode={colorMode}
				/>
			}
			userAvatar={userAvatar}
		/>
	);
};

export const DualAvatarWithIdentityCards = ({
	agentName,
	agentUserId,
	userName,
	relatedUserId,
	isOffsetInlineStart,
	stack,
	userBorderColor,
	profileCardTrigger,
	position,
	offset,
	agentAvatar,
	userAvatar,
}: {
	agentName?: string;
	agentUserId?: string;
	userName?: string;
	relatedUserId?: string;
	isOffsetInlineStart: boolean;
	stack: DualAvatarStack;
	userBorderColor?: string;
	profileCardTrigger?: ProfileIdentityProps['trigger'];
	position?: ProfileIdentityProps['position'];
	offset?: ProfileIdentityProps['offset'];
	agentAvatar: React.ReactNode;
	userAvatar: React.ReactNode;
}): React.JSX.Element => {
	const { rootRef, cover } = useAvatarItemCover();
	const coverStyle: React.CSSProperties = {
		top: cover.top,
		left: cover.left,
		width: cover.width,
		height: cover.height,
	};
	const visualStackStyle: React.CSSProperties = {
		top: -cover.top,
		left: -cover.left,
	};
	const userTarget =
		stack === 'default' ? (
			<div
				data-testid="agent-user-dual-avatar-user-frame"
				css={dualAvatarUserDefaultFrameStyles}
				style={{ '--agent-user-dual-avatar-user-border': userBorderColor } as React.CSSProperties}
				{...stackUserFrameAttrs(stack)}
			>
				<Box
					testId="agent-user-dual-avatar-user-target"
					xcss={cx(styles.dualAvatarTriggerFill, styles.dualAvatarTriggerTarget)}
					{...{ 'data-analytics-card-pointer': 'auto' }}
				>
					<Box xcss={styles.dualAvatarUserDefaultInner}>
						<div css={dualAvatarUserFaceClipStyles}>
							<div css={dualAvatarUserAvatarShiftStyles}>{userAvatar}</div>
						</div>
					</Box>
				</Box>
			</div>
		) : stack === 'large' ? (
			<div
				data-testid="agent-user-dual-avatar-user-frame"
				css={dualAvatarUserLargeFrameStyles}
				style={{ '--agent-user-dual-avatar-user-border': userBorderColor } as React.CSSProperties}
				{...stackUserFrameAttrs(stack)}
			>
				<Box
					testId="agent-user-dual-avatar-user-target"
					xcss={cx(styles.dualAvatarTriggerFill, styles.dualAvatarTriggerTarget)}
					{...{ 'data-analytics-card-pointer': 'auto' }}
				>
					<div css={dualAvatarUserFaceClipStyles}>
						<div css={dualAvatarUserAvatarShiftStyles}>{userAvatar}</div>
					</div>
				</Box>
			</div>
		) : (
			<Box
				testId="agent-user-dual-avatar-user-target"
				xcss={cx(styles.dualAvatarUser, styles.dualAvatarTriggerTarget)}
				{...{ 'data-analytics-card-pointer': 'auto' }}
			>
				{userAvatar}
			</Box>
		);

	return (
		<Box
			ref={rootRef}
			testId="agent-user-dual-avatar"
			xcss={
				stack === 'default'
					? isOffsetInlineStart
						? styles.dualAvatarDefaultOffset
						: styles.dualAvatarDefault
					: stack === 'large'
						? isOffsetInlineStart
							? styles.dualAvatarLargeOffset
							: styles.dualAvatarLarge
						: isOffsetInlineStart
							? styles.dualAvatarOffsetInlineStart
							: styles.dualAvatar
			}
			{...{
				'data-analytics-card-anchor': 'avatar-item',
				'data-stack': stack,
				...stackRootAttrs(stack),
			}}
		>
			<div
				css={dualAvatarCardTriggerStyles}
				data-testid="agent-user-dual-avatar-agent-anchor"
				data-analytics-card-anchor="avatar-item"
				style={coverStyle}
			>
				<ProfileIdentity
					userId={agentUserId}
					fullName={agentName}
					agent={{}}
					trigger={profileCardTrigger}
					position={position}
					offset={offset}
				>
					<Box
						testId="agent-user-dual-avatar-agent-trigger"
						xcss={styles.dualAvatarTriggerFill}
						{...{
							'data-analytics-card-fill': 'avatar-item',
							'data-analytics-card-pointer': 'none',
						}}
					>
						<Box
							xcss={
								stack === 'default'
									? styles.dualAvatarVisualStackDefault
									: stack === 'large'
										? styles.dualAvatarVisualStackLarge
										: styles.dualAvatarVisualStack
							}
							style={visualStackStyle}
						>
							<Box
								testId="agent-user-dual-avatar-agent-target"
								xcss={cx(
									stack === 'default'
										? styles.dualAvatarAgentDefault
										: stack === 'large'
											? styles.dualAvatarAgentLarge
											: styles.dualAvatarAgent,
									styles.dualAvatarTriggerTarget,
								)}
								{...{ 'data-analytics-card-pointer': 'auto', ...stackAgentAttrs(stack) }}
							>
								{agentAvatar}
							</Box>
						</Box>
					</Box>
				</ProfileIdentity>
			</div>
			<div
				css={dualAvatarCardTriggerStyles}
				data-testid="agent-user-dual-avatar-user-anchor"
				data-analytics-card-anchor="avatar-item"
				style={coverStyle}
			>
				<ProfileIdentity
					userId={relatedUserId}
					fullName={userName}
					trigger={profileCardTrigger}
					position={position}
					offset={offset}
				>
					<Box
						testId="agent-user-dual-avatar-user-trigger"
						xcss={styles.dualAvatarTriggerFill}
						{...{
							'data-analytics-card-fill': 'avatar-item',
							'data-analytics-card-pointer': 'none',
						}}
					>
						<Box
							xcss={
								stack === 'default'
									? styles.dualAvatarVisualStackDefault
									: stack === 'large'
										? styles.dualAvatarVisualStackLarge
										: styles.dualAvatarVisualStack
							}
							style={visualStackStyle}
						>
							{userTarget}
						</Box>
					</Box>
				</ProfileIdentity>
			</div>
		</Box>
	);
};
